import moment from 'moment';
import format from 'date-fns/format';
import {
  QuestionTextField,
  QuestionRadioButtonsField,
  QuestionDateDropdownField,
  QuestionYesNoField
} from 'components/Questionnaires/fields';
import {
  QuestionAddressBlock,
  QuestionEntityTypeBlock,
  QuestionSignerBlock,
  QuestionTaxIdentificationNumbersBlock,
  QuestionCloselyHeldInvestmentVehicleBlock,
  QuestionNonReportingIgaBlock,
  QuestionDreBlock
} from 'components/Questionnaires/blocks';
import { requiredValidator } from 'components/Questionnaires/validators';
import { getKey, DocumentKeys } from 'common/forms/documentTypes';
import { uploadSupportingDocField } from 'common/questionnaires/definitions/fields';
import {
  usPersonOrIntermediaryBlock,
  incorporationCountryBlock,
  publiclyTradedNffeBlock,
  nonqualifiedIntermediaryBlock,
  nonwithholdingBlock,
  qualifiedIntermediaryBlock
} from './blocks/blocks';
import { NonwithholdingTypes } from './blocks/nonwithholdingBlock';

const isCh3AndCh4Selected = qAnswers => (qAnswers?.entityType?.ch3Status && qAnswers?.entityType?.ch4Status);

// CH3 Selections:
const isCh3BlockVisible = (qAnswers, ch3Status) => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch3Status === ch3Status;
const isCertainUsBranchesVisible = qAnswers => isCh3BlockVisible(qAnswers, 'UsBranch');
const isCh3TerritoryFinancialInstitutionVisible = qAnswers => isCh3BlockVisible(qAnswers, 'TerritoryFinancialInstitution');
const isQualifiedIntermediaryVisible = qAnswers => isCh3BlockVisible(qAnswers, 'QualifiedIntermediary');
const isNonqualifiedIntermediaryVisible = qAnswers => isCh3BlockVisible(qAnswers, 'NonQualifiedIntermediary');
const isNonwithholdingForeignPartnershipVisible = qAnswers => isCh3BlockVisible(qAnswers, 'NonwithholdingForeignPartnership');
const isNonwithholdingForeignSimpleTrustVisible = qAnswers => isCh3BlockVisible(qAnswers, 'NonwithholdingForeignSimpleTrust');
const isNonwithholdingForeignGrantorTrustVisible = qAnswers => isCh3BlockVisible(qAnswers, 'NonwithholdingForeignGrantorTrust');

// CH4 Selections:
const isCh4BlockVisible = (qAnswers, ch4Status) => isCh3AndCh4Selected(qAnswers) && qAnswers?.entityType?.ch4Status === ch4Status;
const isSponsoredFfIVisible = qAnswers => isCh4BlockVisible(qAnswers, 'SponsoredFfi');
const isCertifiedDeemedCompliantVisible = qAnswers => isCh4BlockVisible(qAnswers, 'CertDeemedCompliantSponsored');
const isOwnerDocumentedFfiVisible = qAnswers => isCh4BlockVisible(qAnswers, 'OwnerDocumentedFfi');
const isRestrictedDistributorVisible = qAnswers => isCh4BlockVisible(qAnswers, 'RestrictedDistributor');
const isExemptRetirementPlansVisible = qAnswers => isCh4BlockVisible(qAnswers, 'ExemptRetirementPlans');
const isNonfinancialStartupVisible = qAnswers => isCh4BlockVisible(qAnswers, 'ExceptedNonfinancialStartUpCompany');
const isNonfinancialEntityVisible = qAnswers => isCh4BlockVisible(qAnswers, 'ExceptedNonfinancialEntityInLiquidation');
const isCh4TerritoryFinancialInstitutionVisible = qAnswers => !isCh3TerritoryFinancialInstitutionVisible(qAnswers) && isCh4BlockVisible(qAnswers, 'TerritoryFinancialInstitution');
const isPubliclyTradedNffeVisible = qAnswers => isCh4BlockVisible(qAnswers, 'PubliclyTradedNffeOrNffeAffiliate');
const isNonReportingIgaFfiVisible = qAnswers => isCh4BlockVisible(qAnswers, 'NonreportingIgaFfi');
const isSponsoredNffeVisible = qAnswers => isCh4BlockVisible(qAnswers, 'SponsoredDirectReportingNffe');

// Other:
const isTaxIdentificationNumbersVisible = qAnswers => isCh3AndCh4Selected(qAnswers);
const isSponsorTrusteeVisible = qAnswers => ['CdcFiFfiTrusteeDocumentedTrust', 'CdcFiFfiSpCloselyHeldInvtVehicle', 'CdcFiFfiSpInvtEntityControlledForeignCorp'].includes(qAnswers?.igaSubclassification);
const getUniqueUsTinInfo = (qAnswers) => {
  const ch3StatusesRequiringUniqueInfo = ['QualifiedIntermediary', 'WithholdingForeignPartnership', 'WithholdingForeignTrust', 'TerritoryFinancialInstitution', 'UsBranch'];
  const ch4StatusesRequiringUniqueInfo = ['TerritoryFinancialInstitution'];
  const electionRequiresUniqueInfo = ch3StatusesRequiringUniqueInfo.includes(qAnswers?.entityType?.ch3Status) || ch4StatusesRequiringUniqueInfo.includes(qAnswers?.entityType?.ch4Status);
  return electionRequiresUniqueInfo ? 'questionnaire.w8imy.taxIdentificationNumbers.usTin.info' : undefined;
};
const isUsTinRequired = (qAnswers) => {
  const requiredCh3Statuses = [
    'QualifiedIntermediary',
    'TerritoryFinancialInstitution',
    'UsBranch',
    'WithholdingForeignPartnership',
    'WithholdingForeignTrust'
  ];
  const requiredCh4Statuses = [
    'TerritoryFinancialInstitution'
  ];
  const ch3OrCh4StatusRequiresTin = requiredCh3Statuses.includes(qAnswers?.entityType?.ch3Status) || requiredCh4Statuses.includes(qAnswers?.entityType?.ch4Status);
  if (ch3OrCh4StatusRequiresTin === true) return true;

  if (qAnswers?.nonqualifiedIntermediary?.isQsl === 'Yes') return true;

  return false;
};

const isFtinRequired = (qAnswers) => {
  const isQdd = qAnswers?.qualifiedIntermediary?.entity === '2' || qAnswers?.qualifiedIntermediary?.entity === '3';

  return isQdd;
};

const w8Imy = {
  blocks: [
    {
      id: 'organizationName',
      title: 'questionnaire.w8imy.name',
      component: undefined,
      visible: true,
      questions: [
        {
          id: 'name',
          label: 'questionnaire.w8imy.name.label',
          info: {
            instructions: 'questionnaire.w8imy.name.info'
          },
          component: QuestionTextField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.w8imy.name.errorMessage' }
            ]
          }
        },
      ]
    },
    incorporationCountryBlock(),
    {
      id: 'address',
      title: 'questionnaire.address',
      component: QuestionAddressBlock,
      componentProps: {
        line1Label: 'questionnaire.address.1.label'
      },
      info: {
        instructions: 'questionnaire.address.1.info'
      },
      visible: true,
      lockGroup: 'permanentAddress'
    },
    {
      id: 'mailingAddress',
      title: 'questionnaire.mailingAddress',
      component: QuestionAddressBlock,
      componentProps: {
        line1Label: 'questionnaire.mailingAddress.1.label',
        optional: true
      },
      info: {
        instructions: 'questionnaire.mailingAddress.1.info'
      },
      visible: true,
      linkBlock: true,
      lockGroup: 'mailingAddress'
    },
    {
      id: 'entityType',
      title: 'questionnaire.entityType',
      component: QuestionEntityTypeBlock,
      label: 'questionnaire.entityType.label',
      info: {
        instructions: 'questionnaire.entityType.info',
      },
      visible: true
    },
    {
      id: 'disregardedEntity',
      title: 'questionnaire.disregardedEntity',
      component: QuestionDreBlock,
      componentProps: {
        optional: true,
        dreNameLabel: 'questionnaire.disregardedEntity.name.label',
        dreTypeLabel: 'questionnaire.disregardedEntity.type.label',
        dreGiinLabel: 'questionnaire.giin.label'
      },
      visible: true,
      linkBlock: true
    },
    qualifiedIntermediaryBlock({ visible: isQualifiedIntermediaryVisible }),
    nonqualifiedIntermediaryBlock({ visible: isNonqualifiedIntermediaryVisible }),
    nonwithholdingBlock({
      nonwithholdingType: NonwithholdingTypes.FOREIGN_PARTNERSHIP,
      visible: isNonwithholdingForeignPartnershipVisible
    }),
    nonwithholdingBlock({
      nonwithholdingType: NonwithholdingTypes.FOREIGN_SIMPLE_TRUST,
      visible: isNonwithholdingForeignSimpleTrustVisible
    }),
    nonwithholdingBlock({
      nonwithholdingType: NonwithholdingTypes.FOREIGN_GRANTOR_TRUST,
      visible: isNonwithholdingForeignGrantorTrustVisible
    }),
    usPersonOrIntermediaryBlock({
      id: 'usBranches',
      title: 'questionnaire.w8imy.usBranches',
      info: {
        instructions: 'questionnaire.w8imy.usBranches.info'
      },
      visible: isCertainUsBranchesVisible
    }),
    usPersonOrIntermediaryBlock({
      id: 'territoryFiCh3',
      title: 'questionnaire.w8imy.territoryFi',
      info: {
        instructions: 'questionnaire.w8imy.territoryFi.info',
      },
      visible: isCh3TerritoryFinancialInstitutionVisible,
    }),
    {
      id: 'sponsoredFfi',
      title: 'questionnaire.ch4.sponsoredFfI',
      component: undefined,
      visible: isSponsoredFfIVisible,
      questions: [
        {
          id: 'answer',
          component: QuestionYesNoField,
          visible: true,
          label: 'questionnaire.ch4.sponsoredFfI.investmentEntity.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.sponsoredFfI.investmentEntity.errorMessage' }
            ]
          }
        },
        {
          id: 'name',
          label: 'questionnaire.ch4.sponsoredFfI.name.label',
          placeholder: 'questionnaire.ch4.sponsoredFfI.name.placeholder',
          info: {
            instructions: 'questionnaire.ch4.sponsoredFfI.name.info'
          },
          component: QuestionTextField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.sponsoredFfI.name.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'sponsoredNffe',
      title: 'questionnaire.ch4.sponsoredNffe',
      component: undefined,
      visible: isSponsoredNffeVisible,
      questions: [
        {
          id: 'name',
          label: 'questionnaire.ch4.sponsoredFfI.name.label',
          placeholder: 'questionnaire.ch4.sponsoredFfI.name.placeholder',
          info: {
            instructions: 'questionnaire.ch4.sponsoredFfI.name.info'
          },
          component: QuestionTextField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.sponsoredFfI.name.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'investmentVehicle',
      title: 'questionnaire.ch4.investmentVehicle',
      component: QuestionCloselyHeldInvestmentVehicleBlock,
      visible: isCertifiedDeemedCompliantVisible,
    },
    {
      id: 'ownerDocumentedFfi',
      title: 'questionnaire.ch4.ownerDocumentedFfi',
      component: undefined,
      visible: isOwnerDocumentedFfiVisible,
      info: {
        instructions: 'questionnaire.ch4.ownerDocumentedFfi.info',
      },
      questions: [
        {
          id: 'items',
          component: QuestionRadioButtonsField,
          componentProps: {
            items: 'questionnaire.ch4.ownerDocumentedFfi.items',
            inputName: 'ownerDocumentedFfi'
          },
          label: 'questionnaire.ch4.ownerDocumentedFfi.label',
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.ownerDocumentedFfi.errorMessage' }
            ]
          }
        },
        uploadSupportingDocField({
          id: 'ownerReportingStatement',
          docType: getKey(DocumentKeys.OWNER_REPORTING_STATEMENT),
          visible: bAnswers => bAnswers?.items === '1'
        }),
        uploadSupportingDocField({
          id: 'auditorsLetter',
          docType: getKey(DocumentKeys.AUDITORS_LETTER),
          visible: bAnswers => bAnswers?.items === '2'
        }),
      ]
    },
    {
      id: 'restrictedDistributor',
      title: 'questionnaire.ch4.restrictedDistributor',
      component: undefined,
      visible: isRestrictedDistributorVisible,
      info: {
        instructions: 'questionnaire.ch4.restrictedDistributor.info',
      },
      questions: [
        {
          id: 'items',
          component: QuestionRadioButtonsField,
          componentProps: {
            items: 'questionnaire.ch4.restrictedDistributor.items',
            inputName: 'restrictedDistributor'
          },
          label: 'questionnaire.ch4.restrictedDistributor.label',
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.restrictedDistributor.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'exemptRetirementPlans',
      title: 'questionnaire.ch4.exemptRetirementPlans',
      component: undefined,
      visible: isExemptRetirementPlansVisible,
      info: {
        instructions: 'questionnaire.ch4.exemptRetirementPlans.info',
      },
      questions: [
        {
          id: 'items',
          component: QuestionRadioButtonsField,
          componentProps: {
            items: 'questionnaire.ch4.exemptRetirementPlans.items',
            inputName: 'exemptRetirementPlans'
          },
          label: 'questionnaire.ch4.exemptRetirementPlans.label',
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.exemptRetirementPlans.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'nonfinancialStartup',
      title: 'questionnaire.ch4.nonfinancialStartup',
      component: undefined,
      visible: isNonfinancialStartupVisible,
      questions: [
        {
          id: 'date',
          component: QuestionDateDropdownField,
          componentProps: {
            minDate: format(moment().subtract(125, 'years').toDate(), 'MM-dd-yyyy'),
            maxDate: format(moment().subtract(1, 'days').toDate(), 'MM-dd-yyyy'),
            dateFormat: 'MM-dd-yyyy',
            showToolTip: true
          },
          visible: true,
          label: 'questionnaire.ch4.nonfinancialStartup.label',
          info: {
            footnote: 'questionnaire.ch4.nonfinancialStartup.footnote'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.nonfinancialStartup.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'nonfinancialEntity',
      title: 'questionnaire.ch4.nonfinancialEntity',
      component: undefined,
      visible: isNonfinancialEntityVisible,
      questions: [
        {
          id: 'date',
          component: QuestionDateDropdownField,
          componentProps: {
            minDate: format(moment().subtract(125, 'years').toDate(), 'MM-dd-yyyy'),
            maxDate: format(moment().subtract(1, 'days').toDate(), 'MM-dd-yyyy'),
            dateFormat: 'MM-dd-yyyy',
            showToolTip: true
          },
          visible: true,
          label: 'questionnaire.ch4.nonfinancialEntity.label',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.ch4.nonfinancialEntity.errorMessage' }
            ]
          }
        }
      ]
    },
    usPersonOrIntermediaryBlock({
      id: 'territoryFiCh4',
      title: 'questionnaire.w8imy.territoryFi',
      info: {
        instructions: 'questionnaire.w8imy.territoryFi.info',
      },
      visible: isCh4TerritoryFinancialInstitutionVisible,
      isCh4Block: true
    }),
    publiclyTradedNffeBlock({ isVisible: isPubliclyTradedNffeVisible }),
    {
      id: 'nonReportingIgaFfi',
      title: 'questionnaire.ch4.nonReportingIgaFfi',
      component: QuestionNonReportingIgaBlock,
      componentProps: {
        sponsorTrusteeProps: {
          visible: isSponsorTrusteeVisible,
        },
      },
      visible: isNonReportingIgaFfiVisible,
      info: {
        instructions: 'questionnaire.ch4.nonReportingIgaFfi.info'
      },
    },
    {
      id: 'taxIdentificationNumbers',
      title: 'questionnaire.taxIdentificationNumbers',
      component: QuestionTaxIdentificationNumbersBlock,
      visible: isTaxIdentificationNumbersVisible,
      componentProps: {
        usTinProps: {
          required: qAnswers => isUsTinRequired(qAnswers),
          info: getUniqueUsTinInfo
        },
        ftinProps: {
          required: qAnswers => isFtinRequired(qAnswers)
        }
      }
    },
    {
      id: 'signer',
      title: 'questionnaire.signer',
      component: QuestionSignerBlock,
      visible: true
    }
  ]
};

export default w8Imy;
